import type { Action } from '@reduxjs/toolkit';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { OrderedSet, type RecordOf } from 'immutable';
import _merge from 'lodash/merge';
import queryString from 'query-string';
import type { SagaIterator, Task } from 'redux-saga';
import {
  type ActionPattern,
  call,
  cancel,
  cancelled,
  delay,
  fork,
  put,
  race,
  select,
  spawn,
  take,
} from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

import { coreSettings, crashReporterInterface, getLogger, httpClientInterface } from '../../utils/core';
import { getItemErrorCodes, getRequestIds } from '../../utils/core/axiosErrorGrouping';
import { getErrorResponse, getHttpStatus, getQcsErrorCode, isDemoModeError } from '../../utils/core/httpErrors';
import { getSafeCode } from '../../utils/core/telemetrySafe';
import type RootState from '../../utils/redux-store/rootState';
import { bulkWritesActions } from '../bulk-writes';
import { coreActions } from '../core-actions';
import { postponedActionsActions, postponedActionsHelpers } from '../postponed-actions';
import { undoActions, undoTypes } from '../undo';
import { cpMetaForResponseAction } from './actionHelpers';
import {
  type BaseResource,
  createTypedQcsSyncResourcePayloadFactory,
  type IdentityLike,
  isRecordOf,
  type QcsBatchResponseItem,
  type QcsResourceResponseData,
  type QcsResourcesResponse,
  type QcsSyncResourcesConfig,
  type QcsSyncResourcesConfigProps,
  type QcsSyncResourcesPayloadProps,
  type ResourceBatch,
  type ResourceSagaAction,
  type ResponseMeta,
  type SagaCallback,
} from './resourceStoreTypes';

const log = getLogger('data/qcsSyncResourceSagaHelpers');

// What a report may say about a failed step. Resources and responses hold customer data, and a React Native response's
// request holds the auth header. The request itself succeeded, so nothing else links this report to Splunk.
const getFailedStepDetails = (
  config: { resourceName: string },
  step: string,
  {
    resource,
    resources,
    response,
  }: {
    resource?: { id?: unknown } | null;
    resources?: ReadonlyArray<unknown> | { count(): number };
    response?: { config?: unknown; status?: number } | null;
  } = {},
) => ({
  custom: {
    itemCount: resources && ('count' in resources ? resources.count() : resources.length),
    resourceId: getSafeCode(resource?.id),
    resourceName: config.resourceName,
    status: response?.status,
    step,
  },
  ...(response && { network: getRequestIds({ config: response.config, response }) }),
});

// Only the user-facing snackbar is suppressed for these codes. The failure action and the
// caller's promise rejection still fire, so optimistic updates roll back, waiters settle, and
// the refresh-token exchange can release the requests parked behind it (SIMPL-35357).
const errorCodesToSuppressSnackbar = [
  // the following error codes are related to a user changing their password
  // we don't want to show a snackbar message in this case
  'QCS-0401-2', // the token expired, was revoked, or the token id is incorrect
  'QCS-0401-19', // the refresh token is invalid
];

type ResourceResult<T extends BaseResource> = ReturnType<
  QcsSyncResourcesConfigProps<T>['transformResourceWithResponse']
>;
type BatchResult<T extends BaseResource> = ReturnType<QcsSyncResourcesConfigProps<T>['transformResourcesWithResponse']>;

// The sagas resolve with the response, after attaching what the write saved.
type ResourceResponse<T extends BaseResource> = AxiosResponse<QcsResourceResponseData<T>> & {
  resource?: ResourceResult<T>;
};
type GetResourceResponse<T extends BaseResource> = AxiosResponse<T> & { resource?: RecordOf<T> | null };
type MatchedResource<T extends BaseResource> = {
  id?: string | null;
  clientId?: string | null;
  resource: RecordOf<T> | T;
};
type BatchResponse<T extends BaseResource> = AxiosResponse<T & { items: QcsBatchResponseItem<T>[] }> & {
  resources?: MatchedResource<T>[];
};

// One record type per call, because a single payload factory can't be typed for every resource type.
const mkResourcesPayload = <T extends BaseResource>(values: Partial<QcsSyncResourcesPayloadProps<T>>) =>
  createTypedQcsSyncResourcePayloadFactory<T>()(values);

// lodash merge returns its first argument, or a new object when that is undefined, so this is never `{}`.
const mergeAxiosConfig = (config: AxiosRequestConfig | undefined, action: ResourceSagaAction): AxiosRequestConfig =>
  _merge(config, action.meta?.axiosConfig) ?? {};

function* onFailure<T extends BaseResource, Command>(
  action: ResourceSagaAction,
  error: unknown,
  config: QcsSyncResourcesConfig<T, Command>,
  failureActionPayload: unknown,
): SagaIterator<void> {
  const errorCode = getQcsErrorCode(error);
  const ignoreError =
    (errorCode !== undefined && errorCodesToSuppressSnackbar.includes(errorCode)) ||
    (typeof config.ignoreError === 'function' ? config.ignoreError(error, action, config) : config.ignoreError);
  if (ignoreError !== true) {
    if (!isDemoModeError(error)) {
      yield put(
        undoActions.addUndo(
          undoTypes.mkUndo({
            userMessage:
              typeof ignoreError === 'string' ? ignoreError : 'Sorry, but we were unable to make your changes.',
            key: uuidv4(),
          }),
        ),
      );
    } else {
      yield put(
        undoActions.addUndo(
          undoTypes.mkUndo({
            userMessage: 'Sorry, but this function is not available in the Demo Mode.',
            key: uuidv4(),
          }),
        ),
      );
    }
  }
  if (config.failureAction) {
    yield put(config.failureAction(failureActionPayload, cpMetaForResponseAction({ ...action.meta, config })));
  }
  action.meta?.reject?.(error);
}

/** Returns whether the action was queued for retry rather than failed outright. */
function* handleError<T extends BaseResource, Command = never>(
  action: ResourceSagaAction,
  error: unknown,
  config: QcsSyncResourcesConfig<T, Command>,
  failureActionPayload: unknown,
): SagaIterator<boolean> {
  if (
    !isDemoModeError(error) &&
    (typeof config.handleRetry === 'function' ? config.handleRetry(error, action, config) : config.handleRetry) &&
    postponedActionsHelpers.shouldPostponeAction(action, error)
  ) {
    yield put(
      postponedActionsActions.addPostponedAction({
        action,
        error,
        onFailure: function* _onFailure() {
          yield call(onFailure<T, Command>, action, error, config, failureActionPayload);
        },
      }),
    );
    return true;
  }
  yield call(onFailure<T, Command>, action, error, config, failureActionPayload);
  return false;
}

const emptyAction: ResourceSagaAction<null> = { type: '', meta: null, payload: null, asyncDispatch: undefined };

export function* qcsSyncGetResources<T extends BaseResource>(
  config: QcsSyncResourcesConfig<T>,
  inAction?: ResourceSagaAction,
): SagaIterator<void> {
  const action = inAction || emptyAction;
  try {
    const base = httpClientInterface.getServiceUrl();
    const resourceUrl =
      typeof config.resourceBaseUrl === 'function' ? config.resourceBaseUrl(action) : config.resourceBaseUrl;
    let url = new URL(resourceUrl, base);
    let requestUrl: string | undefined = url.href;

    const queryParams = queryString.parse(url.search);
    let lastSyncDate: string | null | undefined;
    if (config.getLastSyncDate) {
      lastSyncDate = yield select(config.getLastSyncDate);
    }

    const limitFF: number | undefined = yield select(featureFlagsSelectors.getFeatureFlag, 'limit');
    const limit = limitFF || 5000;
    const axiosConfig = {
      ...mergeAxiosConfig(config.axiosConfig, action),
      params: {
        ...(lastSyncDate && { modifiedAfter: lastSyncDate }),
        limit,
        ..._merge(config.axiosConfig?.params, action.meta?.axiosConfig?.params),
        ...queryParams,
      },
    };

    const actionSuccessPayload: Pick<
      QcsSyncResourcesPayloadProps<T>,
      'resources' | 'lastSyncDate' | 'replaceCollection'
    > = {
      resources: [],
      lastSyncDate: null,
      ...(!config.getLastSyncDate && { replaceCollection: true }),
    };

    while (requestUrl) {
      const response: AxiosResponse<QcsResourcesResponse<T>> = yield call(
        httpClientInterface.send,
        'GET',
        requestUrl,
        undefined,
        axiosConfig.headers,
        axiosConfig.params,
      );

      // If this is the first page of data, update lastSyncDate with the asOf
      // time from the server so we can send it back the next time we retrieve
      // we retrieve resources.
      //
      requestUrl = undefined;
      if (response.data.metaData) {
        if (response.data.metaData.currentPage === 1) {
          actionSuccessPayload.lastSyncDate = response.data.metaData.asOf;
        }
        if (response.data.metaData.nextLink) {
          url = new URL(response.data.metaData.nextLink, base);
          requestUrl = url.href;
        }
      }

      let resources: RecordOf<T>[] | null | undefined;
      try {
        resources = config.transformResponseToResources(response);
      } catch (error) {
        crashReporterInterface.reportError(
          error,
          getFailedStepDetails(config, 'transformResponseToResources', { response }),
        );
        throw error;
      }

      actionSuccessPayload.resources = actionSuccessPayload.resources.concat(resources || []);
    }
    if (config.successAction) {
      yield put(
        config.successAction(
          mkResourcesPayload<T>(actionSuccessPayload),
          cpMetaForResponseAction({ ...action.meta, config }),
        ),
      );
    }
    action.meta?.resolve?.(actionSuccessPayload.resources);
  } catch (error) {
    log.error(`Error retrieving resource "${config.resourceName}": ${error}`);
    if (config.failureAction) {
      yield put(config.failureAction(error, cpMetaForResponseAction({ ...action.meta, config })));
    }
    action.meta?.reject?.(error);

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    yield put(
      coreActions.setUpdateAllState({
        updateAllFailed: true,
        updateAllMessages: OrderedSet([`Can't load ${config.resourceName}.`]),
      }),
    );
/////////////
  } finally {
    if (yield cancelled()) {
      action.meta?.reject?.(new Error(`Request for ${config.resourceName} was cancelled before completion.`));
    }
  }
}

/** Sends a resource, or for a command endpoint a `Command` payload or none, as a POST. */
export function* qcsSyncCreateResource<T extends BaseResource, Command extends IdentityLike = never>(
  config: QcsSyncResourcesConfig<T, Command>,
  action: ResourceSagaAction<RecordOf<T> | T | Command>,
): SagaIterator<unknown> {
  let response: ResourceResponse<T> | undefined;

  const { payload: resource } = action;
  const path =
    typeof config.resourceBaseUrl === 'function'
      ? config.resourceBaseUrl(action)
      : `${config.resourceBaseUrl}${resource?.id ? `/${resource.id}` : ''}`;
  const base = httpClientInterface.getServiceUrl();
  const url = new URL(path, base);
  const requestUrl = url.href;

  try {
    let requestData;
    try {
      requestData = config.transformResourceToRequestData(resource);
    } catch (error) {
      crashReporterInterface.reportError(
        error,
        getFailedStepDetails(config, 'transformResourceToRequestData', { resource }),
      );
      throw error;
    }
    const axiosConfig = mergeAxiosConfig(config.axiosConfig, action);
    const createResponse: ResourceResponse<T> = yield call(
      httpClientInterface.send,
      'POST',
      requestUrl,
      requestData,
      axiosConfig.headers,
      axiosConfig.params,
    );
    response = createResponse;

    let resultingResource: ResourceResult<T>;
    try {
      resultingResource = config.transformResourceWithResponse(resource, createResponse, config);
    } catch (error) {
      crashReporterInterface.reportError(
        error,
        getFailedStepDetails(config, 'transformResourceWithResponse', { resource, response: createResponse }),
      );
      throw error;
    }
    if (config.successAction) {
      yield put(
        config.successAction(
          resultingResource,
          cpMetaForResponseAction({ ...action.meta, config, response: createResponse }),
        ),
      );
    }
    // Note: This is a bit ugly, but early on we directly passed back response (no resource). Ideally we would have passed
    //       back new object, that contained resource and response but would require more refactoring.
    createResponse.resource = resultingResource;
    action.meta?.resolve?.(createResponse);
  } catch (error) {
    log.error(`Error creating ${config.resourceName}`, error);
    const payload = Object.assign({ clientId: resource?.clientId }, error);
    yield call(handleError<T, Command>, action, error, config, payload);
    return error;
  }

  return response;
}

/** A request's config value, or a callback that builds it from the action that triggered the request. */
type RequestValue<V> = V | SagaCallback<[action: ResourceSagaAction | undefined], V>;
type RequestValues = { readonly [key: string]: unknown };
type RequestBody = string | number | boolean | null | readonly unknown[] | RequestValues;

export type QCSRequestConfig = {
  method: 'GET' | 'PUT' | 'POST' | 'DELETE';
  path: RequestValue<string>;
  data?: RequestValue<RequestBody | undefined>;
  headers?: RequestValue<RequestValues>;
  params?: RequestValue<RequestValues>;
  /** Gets the response; like a resource config's, it may declare the data it expects or take no payload. */
  successAction?: SagaCallback<[payload: AxiosResponse<unknown> | void, meta?: ResponseMeta], Action>;
  failureAction?: (payload: unknown, meta: ResponseMeta) => Action;
  showError?:
    | boolean
    | string
    | SagaCallback<[error: unknown, action: ResourceSagaAction | undefined], boolean | string>;
};
export function* qcsRequest(
  config: QCSRequestConfig,
  action?: ResourceSagaAction,
): SagaIterator<AxiosResponse<unknown> | undefined> {
  let response: AxiosResponse<unknown> | undefined;

  const { method, path, data, headers, params, successAction, failureAction, showError = true } = config;

  const urlPath = typeof path === 'function' ? path(action) : path;
  const urlBase = httpClientInterface.getServiceUrl();
  const url = new URL(urlPath, urlBase);
  const requestData = typeof data === 'function' ? data(action) : data;
  const requestHeaders = typeof headers === 'function' ? headers(action) : headers;
  const requestParams = typeof params === 'function' ? params(action) : params;

  try {
    const requestResponse: AxiosResponse<unknown> = yield call(
      httpClientInterface.send,
      method,
      url.href,
      requestData,
      requestHeaders,
      requestParams,
    );
    response = requestResponse;

    if (successAction) {
      yield put(
        successAction(requestResponse, cpMetaForResponseAction({ ...action?.meta, config, response: requestResponse })),
      );
    }

    action?.meta?.resolve?.(requestResponse);
  } catch (error) {
    verify(isDemoModeError(error), error);
    log.error(`Error ${method} ${urlPath}`, error);
    const resultError = typeof showError === 'function' ? showError(error, action) : showError;
    if (resultError) {
      yield put(
        undoActions.addUndo(
          undoTypes.mkUndo({
            userMessage: typeof showError === 'string' ? showError : 'Sorry, but we were unable to make your changes.',
            key: uuidv4(),
          }),
        ),
      );
    }
    if (failureAction) {
      yield put(failureAction(error, cpMetaForResponseAction({ ...action?.meta, config })));
    }
    action?.meta?.reject?.(error);
  }

  return response;
}

/** Sends a resource, or for a command endpoint a `Command` payload, as a PUT. */
export function* qcsSyncUpdateResource<T extends BaseResource, Command extends NonNullable<IdentityLike> = never>(
  config: QcsSyncResourcesConfig<T, Command>,
  action: ResourceSagaAction<RecordOf<T> | T | Command>,
): SagaIterator<ResourceResponse<T> | undefined> {
  let response: ResourceResponse<T> | undefined;

  const { payload: resource } = action;
  const path =
    typeof config.resourceBaseUrl === 'function'
      ? config.resourceBaseUrl(action)
      : `${config.resourceBaseUrl}${resource.id ? `/${resource.id}` : ''}`;
  const base = httpClientInterface.getServiceUrl();
  const url = new URL(path, base);
  const requestUrl = url.href;

  try {
    let requestData;
    try {
      requestData = config.transformResourceToRequestData(resource);
    } catch (error) {
      crashReporterInterface.reportError(
        error,
        getFailedStepDetails(config, 'transformResourceToRequestData', { resource }),
      );
      throw error;
    }
    const axiosConfig = mergeAxiosConfig(config.axiosConfig, action);
    const updateResponse: ResourceResponse<T> = yield call(
      httpClientInterface.send,
      'PUT',
      requestUrl,
      requestData,
      axiosConfig.headers,
      axiosConfig.params,
    );
    response = updateResponse;

    let resultingResource: ResourceResult<T>;
    try {
      resultingResource = config.transformResourceWithResponse(resource, updateResponse, config);
    } catch (error) {
      crashReporterInterface.reportError(
        error,
        getFailedStepDetails(config, 'transformResourceWithResponse', { resource, response: updateResponse }),
      );
      throw error;
    }
    if (config.successAction) {
      yield put(
        config.successAction(
          resultingResource,
          cpMetaForResponseAction({ ...action.meta, config, response: updateResponse }),
        ),
      );
    }
    updateResponse.resource = resultingResource;
    action.meta?.resolve?.(updateResponse);
  } catch (error) {
    log.error(`Error updating ${config.resourceName}`, error);
    const payload = Object.assign({ clientId: resource.clientId }, error);
    yield call(handleError<T, Command>, action, error, config, payload);
  }

  return response;
}

export function* qcsSyncGetResource<T extends BaseResource>(
  config: QcsSyncResourcesConfig<T>,
  inAction?: ResourceSagaAction<IdentityLike>,
): SagaIterator<unknown> {
  const action = inAction || emptyAction;
  const { payload: resource, asyncDispatch } = action;
  const path =
    typeof config.resourceBaseUrl === 'function'
      ? config.resourceBaseUrl(action)
      : `${config.resourceBaseUrl}${resource?.id ? `/${resource.id}` : ''}`;

  const base = httpClientInterface.getServiceUrl();
  const url = new URL(path, base);
  const requestUrl = url.href;

  let response: GetResourceResponse<T> | undefined;
  try {
    const axiosConfig = mergeAxiosConfig(config.axiosConfig, action);
    const getResponse: GetResourceResponse<T> = yield call(
      httpClientInterface.send,
      'GET',
      requestUrl,
      undefined,
      axiosConfig.headers,
      axiosConfig.params,
    );
    response = getResponse;

    let receivedResource: RecordOf<T> | null | undefined;
    try {
      receivedResource = config.transformResponseToResource(getResponse);
    } catch (error) {
      crashReporterInterface.reportError(
        error,
        getFailedStepDetails(config, 'transformResponseToResource', { response: getResponse }),
      );
      throw error;
    }
    if (config.successAction) {
      yield put(
        config.successAction(
          mkResourcesPayload<T>({ resources: receivedResource ? [receivedResource] : [], asyncDispatch }),
          cpMetaForResponseAction({ ...action.meta, config, response: getResponse }),
        ),
      );
    }
    // Note: This is a bit ugly, but early on we directly passed back response (no resource). Ideally we would have passed
    //       back new object, that contained resource and response but would require more refactoring.
    getResponse.resource = receivedResource;
    action.meta?.resolve?.(getResponse);
  } catch (error) {
    log.error(`Error getting ${config.resourceName}`, error);
    const payload = Object.assign({ id: resource ? resource.id : undefined }, error);
    if (config.failureAction) {
      yield put(config.failureAction(payload, cpMetaForResponseAction({ ...action.meta, config })));
    }
    action.meta?.reject?.(error);
    return payload;
  }

  return response;
}

function* sendResourcesRequest<T extends BaseResource>(
  config: QcsSyncResourcesConfig<T>,
  action: ResourceSagaAction,
  requestUrl: string,
  resources: ResourceBatch<T>,
): SagaIterator<SendResourcesResult<T>> {
  let requestData;
  try {
    requestData = config.transformResourcesToRequestData(resources);
  } catch (error) {
    crashReporterInterface.reportError(
      error,
      getFailedStepDetails(config, 'transformResourcesToRequestData', { resources }),
    );
    throw error;
  }
  const axiosConfig = mergeAxiosConfig(config.axiosConfig, action);
  const response: BatchResponse<T> = yield call(
    httpClientInterface.send,
    'PUT',
    requestUrl,
    requestData,
    axiosConfig.headers,
    axiosConfig.params,
  );

  let resultingResources: BatchResult<T>;
  try {
    resultingResources = config.transformResourcesWithResponse(resources, response);
  } catch (error) {
    if (Array.isArray(error)) {
      // report once per batch — reporting each item separately floods the
      // crash reporter with payload-heavy notifies on large failed batches
      crashReporterInterface.reportError(
        new Error(`${config.resourceName} batch update failed with ${error.length} item errors`),
        {
          network: getRequestIds({ config: response.config, response }),
          payload: {
            itemErrorCount: error.length,
            itemErrorCodes: getItemErrorCodes(response.data),
            responseStatus: response.status,
          },
        },
        { groupingHash: `qcs-sync-update-resources:${config.resourceName}:item-errors` },
      );
    } else {
      crashReporterInterface.reportError(
        error,
        getFailedStepDetails(config, 'transformResourcesWithResponse', { resources, response }),
      );
    }
    throw error;
  }

  return { response, resultingResources };
}

interface SendResourcesResult<T extends BaseResource> {
  response: BatchResponse<T>;
  resultingResources: BatchResult<T>;
}

// Collapses the responses of a split payload into the single response shape callers expect. The last
// response is spread so anything the http layer attached survives, and `items` must be a real array
// because mergeResultingResourcesIntoResponse reduces over it unguarded.
function mergeSplitResults<T extends BaseResource>(results: SendResourcesResult<T>[]): SendResourcesResult<T> {
  const lastResponse = results[results.length - 1].response;
  const items = results.flatMap((result) => result.response.data?.items ?? []);
  return {
    response: { ...lastResponse, data: { ...lastResponse.data, items } },
    resultingResources: results.flatMap((result) =>
      Array.isArray(result.resultingResources) ? result.resultingResources : [],
    ),
  };
}

const isResourceArray = <T extends BaseResource>(
  resources: ResourceBatch<T>,
): resources is ReadonlyArray<RecordOf<T> | T> => Array.isArray(resources);

export function* qcsSyncUpdateResources<T extends BaseResource>(
  config: QcsSyncResourcesConfig<T>,
  action: ResourceSagaAction<ResourceBatch<T>>,
): SagaIterator<void> {
  const { payload: resources } = action;
  const base = httpClientInterface.getServiceUrl();
  const resourceUrl =
    typeof config.resourceBaseUrl === 'function' ? config.resourceBaseUrl(action) : config.resourceBaseUrl;
  const url = new URL(resourceUrl, base);
  const requestUrl = url.href;

  // Only split opted-in configs, and only array payloads (Immutable collections have no `length`).
  // At or below the limit the payload goes out as the single request it always did.
  const chunkSize =
    config.maxResourcesPerRequest && isResourceArray(resources) && resources.length > config.maxResourcesPerRequest
      ? config.maxResourcesPerRequest
      : 0;
  const chunks =
    chunkSize && isResourceArray(resources)
      ? Array.from({ length: Math.ceil(resources.length / chunkSize) }, (_unused, index) =>
          resources.slice(index * chunkSize, (index + 1) * chunkSize),
        )
      : [resources];
  const resourceCount = isResourceArray(resources) ? resources.length : resources.count();

  // Only a split write gets published, so consumers can treat a tracked operation as "large enough
  // that the user is waiting on several requests" without a timer to suppress small edits.
  const operationId = chunks.length > 1 ? uuidv4() : undefined;

  // Sequential, so the service handles one bounded slice at a time and a failure leaves a
  // well-defined committed prefix. Declared out here so the failure report can count it.
  const results: SendResourcesResult<T>[] = [];
  let failure: { committed: number; retryQueued: boolean } | undefined;

  try {
    for (const [index, chunk] of chunks.entries()) {
      if (operationId) {
        // Published before the request goes out, counting the slice now in flight: the work is
        // under way, and starting at zero would misreport a batch that is already being written.
        // The last chunk is short whenever the payload isn't a multiple of the chunk size.
        const submitted = Math.min((index + 1) * chunkSize, resourceCount);
        yield put(
          index === 0
            ? bulkWritesActions.bulkWriteStarted({
                id: operationId,
                resourceName: config.resourceName,
                total: resourceCount,
                submitted,
              })
            : bulkWritesActions.bulkWriteProgressed({ id: operationId, submitted }),
        );
      }

      try {
        const result: SendResourcesResult<T> = yield call(sendResourcesRequest<T>, config, action, requestUrl, chunk);
        results.push(result);
      } catch (error) {
        if (chunks.length > 1) {
          crashReporterInterface.reportError(
            new Error(
              `${config.resourceName} batch update failed on request ${results.length + 1} of ${chunks.length}`,
            ),
            {
              payload: {
                requestIndex: results.length,
                requestCount: chunks.length,
                chunkSize,
                resourceCount,
                committedResourceCount: results.length * chunkSize,
                responseStatus: getHttpStatus(error),
                errorCode: getSafeCode(getQcsErrorCode(error)),
                ...(Array.isArray(error) && { itemErrorCount: error.length }),
              },
            },
            { groupingHash: `qcs-sync-update-resources:${config.resourceName}:split-request-failure` },
          );
        }
        throw error;
      }
    }

    // One success action and one resolve for the whole payload, so the reducer's optimistic update and
    // undo bookkeeping see the same shape as an unsplit batch. An unsplit payload passes on the exact
    // response and `resultingResources` value it got, which callers resolving on identity depend on and
    // which is not always an array.
    const { response, resultingResources } = chunks.length === 1 ? results[0] : mergeSplitResults(results);
    if (config.successAction) {
      yield put(
        config.successAction(resultingResources, cpMetaForResponseAction({ ...action.meta, config, response })),
      );
    }
    // Note: This is a bit ugly, but early on we directly passed back response (no resources). Ideally we would have passed
    //       back new object, that contained resources and response but would require more refactoring.
    mergeResultingResourcesIntoResponse(resultingResources, response);
    action.meta?.resolve?.(response);
  } catch (error) {
    log.error(`Error batch ${config.resourceName}`, error);
    // A queued retry is still reported: nothing replays it on its own, so from the user's side the
    // write has stopped either way, and how much of it landed is the part nothing else can tell
    // them. Which of the two it was only changes what the toast advises doing about it.
    const retryQueued: boolean = yield call(handleError<T>, action, error, config, error);
    failure = { committed: results.length * chunkSize, retryQueued };
  } finally {
    // Owns the whole lifetime, so a cancelled saga can't strand the operation as permanently
    // pending. A failure hands off to the failed state instead, which the UI clears once it has
    // reported it; a partial write is worth telling the user about rather than vanishing.
    if (operationId) {
      yield put(
        failure
          ? bulkWritesActions.bulkWriteFailed({ id: operationId, ...failure })
          : bulkWritesActions.bulkWriteFinished(operationId),
      );
    }
  }
}

export function mergeResultingResourcesIntoResponse<T extends BaseResource>(
  resultingResources: BatchResult<T>,
  response: BatchResponse<T>,
): void {
  const { items } = response.data;
  const resources = items.reduce<MatchedResource<T>[]>((acc, item) => {
    const itemResource = item.resource;
    const matchedResultingResource = resultingResources?.find((resultingResource) => {
      if (itemResource?.id && resultingResource?.id) {
        // check if match by id if both exists
        return itemResource.id === resultingResource.id;
      } else if (itemResource?.clientId && resultingResource?.clientId) {
        // check if match by clientId if both exists
        return itemResource.clientId === resultingResource.clientId;
      }
      // no match found
      log.warn(`No match found for resource ${itemResource?.id || itemResource?.clientId}`);
      return false;
    });
    if (matchedResultingResource) {
      acc.push({ id: itemResource?.id, clientId: itemResource?.clientId, resource: matchedResultingResource });
    }
    return acc;
  }, []);
  response.resources = resources;
}

// =================================================================================================
// Delete Resource
// =================================================================================================
export function* qcsSyncDeleteResource<T extends BaseResource>(
  config: QcsSyncResourcesConfig<T>,
  action: ResourceSagaAction<RecordOf<T> | T>,
): SagaIterator<ResourceResponse<T> | undefined> {
  const { payload: resource } = action;

  const path =
    typeof config.resourceBaseUrl === 'function'
      ? config.resourceBaseUrl(action)
      : `${config.resourceBaseUrl}${resource?.id ? `/${resource.id}` : ''}`;
  const base = httpClientInterface.getServiceUrl();
  const url = new URL(path, base);
  const requestUrl = url.href;

  let response: ResourceResponse<T> | undefined;
  try {
    const axiosConfig = mergeAxiosConfig(config.axiosConfig, action);
    const deleteResponse: ResourceResponse<T> = yield call(
      httpClientInterface.send,
      'DELETE',
      requestUrl,
      undefined,
      axiosConfig.headers,
      axiosConfig.params,
    );
    response = deleteResponse;

    let resultingResource: ResourceResult<T>;
    try {
      resultingResource = markDeleted(config.transformResourceWithResponse(resource, deleteResponse, config));
    } catch (error) {
      crashReporterInterface.reportError(
        error,
        getFailedStepDetails(config, 'transformResourceWithResponse', { resource, response: deleteResponse }),
      );
      throw error;
    }

    if (config.successAction) {
      yield put(
        config.successAction(
          resultingResource,
          cpMetaForResponseAction({ ...action.meta, config, response: deleteResponse }),
        ),
      );
    }
    action.meta?.resolve?.(deleteResponse);
  } catch (error) {
    log.error(`Error deleting ${config.resourceName}`, error);
    const payload = Object.assign({ id: resource ? resource.id : undefined }, error);
    yield call(handleError<T>, action, error, config, payload);
    return response;
  }

  return response;
}

/** Flags a deleted resource; a delete whose response yields no single resource fails, as it always has. */
function markDeleted<T extends BaseResource>(resource: ResourceResult<T>): RecordOf<T> | T {
  if (!resource || Array.isArray(resource)) {
    throw new TypeError('A deleted resource must be a single resource');
  }
  return isRecordOf(resource) ? resource.set('isDeleted', true) : { ...resource, isDeleted: true };
}

export function* qcsSyncPollResource<T extends BaseResource>(
  config: QcsSyncResourcesConfig<T>,
  action: ResourceSagaAction = emptyAction,
): SagaIterator<void> {
  const base = httpClientInterface.getServiceUrl();
  const resourceUrl =
    typeof config.resourceBaseUrl === 'function' ? config.resourceBaseUrl(action) : config.resourceBaseUrl;
  const url = new URL(resourceUrl, base);
  const requestUrl = url.href;

  let attempt: number;
  let response: AxiosResponse<QcsResourcesResponse<T>> | undefined;
  for (attempt = 0; attempt < config.pollConfig.attempts; attempt += 1, yield delay(config.pollConfig.pause)) {
    try {
      const axiosConfig = mergeAxiosConfig(config.axiosConfig, action);
      const pollResponse: AxiosResponse<QcsResourcesResponse<T>> = yield call(
        httpClientInterface.send,
        'GET',
        requestUrl,
        undefined,
        axiosConfig.headers,
        axiosConfig.params,
      );
      response = pollResponse;

      if (config.pollConfig.isComplete(pollResponse, attempt)) {
        break;
      }
    } catch (error) {
      log.error(`Error polling ${config.resourceName}`, error);
      if (config.pollConfig.isComplete(getErrorResponse(error), attempt)) {
        break;
      }
    }
  }
  let resources;
  try {
    resources = config.transformResponseToResources(response);
  } catch (error) {
    crashReporterInterface.reportError(
      error,
      getFailedStepDetails(config, 'transformResponseToResources', { response }),
    );
    throw error;
  }
  if (attempt < config.pollConfig.attempts) {
    if (config.successAction) {
      yield put(
        config.successAction(
          mkResourcesPayload<T>({ resources: resources || [] }),
          cpMetaForResponseAction({ ...action.meta, config, response }),
        ),
      );
    }
    action.meta?.resolve?.(response);
  } else {
    if (config.failureAction) {
      yield put(
        config.failureAction(
          mkResourcesPayload<T>({ resources: resources || [] }),
          cpMetaForResponseAction({ ...action.meta, config, response }),
        ),
      );
    }
    action.meta?.reject?.(response);
  }
}

export function* takeAndDebounce<Args extends unknown[]>(
  pattern: ActionPattern,
  saga: SagaCallback<[...Args, ResourceSagaAction], unknown>,
  ...args: Args
): SagaIterator<never> {
  let task: Task | undefined;
  let debounced = 0;
  let executed = 0;
  while (true) {
    const action: ResourceSagaAction = yield take(pattern);
    const ms = coreSettings?.fetchDebounceTimeout ?? 0;
    if (!task) {
      executed += 1;
      log.debug(`debounced=${debounced} executed=${executed} ms=${ms}`, action);
      yield call(saga, ...args, action);
      task = yield fork(function* postponeHandling() {
        yield delay(ms);
        task = undefined;
      });
    } else {
      debounced += 1;
      log.debug(`debounced=${debounced} executed=${executed} ms=${ms}`, action);
      yield cancel(task);
      task = yield fork(function* postponeHandling() {
        yield delay(ms);
        debounced -= 1;
        executed += 1;
        log.debug(`debounced=${debounced} executed=${executed} ms=${ms}`, action);
        yield call(saga, ...args, action);
        task = undefined;
      });
    }
  }
}

export function createGreedDebounce(key: string) {
  let task: Task | undefined;
  let handled: boolean | undefined;
  let executed = 0;
  let debounced = 0;
  return {
    callFirst: function* callFirst<Args extends unknown[]>(
      saga: (...args: Args) => unknown,
      ...args: Args
    ): SagaIterator<void> {
      if (!task) {
        const ms = coreSettings?.fetchDebounceTimeout ?? 0;
        handled = true;
        executed += 1;
        log.debug(`debounced=${debounced} executed=${executed} ms=${ms}`, key);
        yield call(saga, ...args);
        task = yield spawn(function* taskSpawn() {
          yield delay(ms);
          task = undefined;
        });
      }
    },
    callLast: function* callLast<Args extends unknown[]>(
      saga: (...args: Args) => unknown,
      ...args: Args
    ): SagaIterator<void> {
      if (task && !handled) {
        const ms = coreSettings?.fetchDebounceTimeout ?? 0;
        debounced += 1;
        log.debug(`debounced=${debounced} executed=${executed} ms=${ms}`, key);
        yield cancel(task);
        task = yield spawn(function* taskSpawn() {
          yield delay(ms);
          task = undefined;
          executed += 1;
          debounced -= 1;
          log.debug(`debounced=${debounced} executed=${executed} ms=${ms}`, key);
          yield call(saga, ...args);
        });
      }
      handled = false;
    },
  };
}

export function* fetchWait(
  resourceSelectors: { getIsLoading: (state: RootState) => unknown },
  successAction: ActionPattern,
  failureAction: ActionPattern,
  timeout = 3000,
): SagaIterator<boolean> {
  const isLoading: unknown = yield select(resourceSelectors.getIsLoading);
  if (isLoading) {
    const { successResult }: { successResult?: unknown } = yield race({
      successResult: take(successAction),
      failureResult: take(failureAction),
      timeoutResult: delay(timeout),
    });
    return Boolean(successResult);
  }
  return true;
}
